// React
import { Suspense } from 'react'
// Routing
import { Redirect, Route, Switch } from 'react-router-dom'
// MainStem - UI
import { Loader } from '@mainstem/react-mainstem'
// Page : Error
import PageErr from 'pages/err'
// Utilities
import { lazyWithRetry } from 'utils/lazyWithRetry'
import PageApril2026RFQ from 'pages/blog/april-2026-rfq'

// Lazy Loaded Pages
const lazyLoadedPages = {
  accounting: {
    pay: {
      invoice: lazyWithRetry(() => import('pages/accounting/pay/invoice'))
    }
  },
  amazon: {
    product: {
      details: lazyWithRetry(() => import('pages/amazon/product/details'))
    },
    shipment: {
      tracking: lazyWithRetry(() => import('pages/amazon/shipment/tracking/page'))
    }
  },
  logistics: {
    receiving: {
      dashboard: lazyWithRetry(() => import('pages/logistics/receiving/dashboard')),
      details: lazyWithRetry(() => import('pages/logistics/receiving/details'))
    },
    shipment: {
      details: lazyWithRetry(() => import('pages/logistics/shipment/details')),
      dashboard: lazyWithRetry(() => import('pages/logistics/shipment/dashboard'))
    }
  },
  organization: {
    catalog: {
      create: lazyWithRetry(() => import('pages/organization/catalog/create')),
      dashboard: lazyWithRetry(() => import('pages/organization/catalog/dashboard')),
      details: lazyWithRetry(() => import('pages/organization/catalog/details')),
      update: lazyWithRetry(() => import('pages/organization/catalog/update'))
    },
    department: {
      create: lazyWithRetry(() => import('pages/organization/department/create')),
      dashboard: lazyWithRetry(() => import('pages/organization/department/dashboard')),
      details: lazyWithRetry(() => import('pages/organization/department/details')),
      update: lazyWithRetry(() => import('pages/organization/department/update'))
    },
    location: {
      create: lazyWithRetry(() => import('pages/organization/location/create')),
      dashboard: lazyWithRetry(() => import('pages/organization/location/dashboard')),
      details: lazyWithRetry(() => import('pages/organization/location/details')),
      update: lazyWithRetry(() => import('pages/organization/location/update'))
    },
    permissionGroup: {
      dashboard: lazyWithRetry(() => import('pages/organization/permission-group/dashboard')),
      details: lazyWithRetry(() => import('pages/organization/permission-group/details')),
      update: lazyWithRetry(() => import('pages/organization/permission-group/update'))
    },
    user: {
      create: lazyWithRetry(() => import('pages/organization/user/create')),
      details: lazyWithRetry(() => import('pages/organization/user/details')),
      dashboard: lazyWithRetry(() => import('pages/organization/user/dashboard')),
      update: lazyWithRetry(() => import('pages/organization/user/update'))
    }
  },

  purchasing: {
    invoices: {
      dashboard: lazyWithRetry(() => import('pages/purchasing/invoices/dashboard')),
      inquiry: lazyWithRetry(() => import('pages/purchasing/invoices/inquiry'))
    },
    punchOut: {
      details: lazyWithRetry(() => import('pages/purchasing/punch-out/details')),
      purchase: lazyWithRetry(() => import('pages/purchasing/punch-out/purchase')),
      settings: lazyWithRetry(() => import('pages/purchasing/punch-out/settings'))
    },
    purchaseOrders: {
      dashboard: lazyWithRetry(() => import('pages/purchasing/purchase-orders/dashboard'))
    },
    requisitions: {
      bulkOrdering: {
        dashboard: lazyWithRetry(() => import('pages/purchasing/requisition/bulk-ordering/dashboard/page')),
        details: lazyWithRetry(() => import('pages/purchasing/requisition/bulk-ordering/details/page')),
        edit: lazyWithRetry(() => import('pages/purchasing/requisition/bulk-ordering/edit/page'))
      },
      dashboard: lazyWithRetry(() => import('pages/purchasing/requisition/dashboard')),
      details: lazyWithRetry(() => import('pages/purchasing/requisition/details')),
      quickorder: lazyWithRetry(() => import('pages/purchasing/requisition/quick-order')),
      submit: lazyWithRetry(() => import('pages/purchasing/requisition/submit')),
      update: lazyWithRetry(() => import('pages/purchasing/requisition/update'))
    },
    rfq: {
      approve: lazyWithRetry(() => import('pages/purchasing/rfq/approve')),
      dashboard: lazyWithRetry(() => import('pages/purchasing/rfq/dashboard')),
      details: lazyWithRetry(() => import('pages/purchasing/rfq/details')),
      update: lazyWithRetry(() => import('pages/purchasing/rfq/update'))
    }
  },
  account: {
    profile: lazyWithRetry(() => import('pages/account/profile/page')),
    profileUpdate: lazyWithRetry(() => import('pages/account/profile/update/page'))
  },
  settings: {
    account: lazyWithRetry(() => import('pages/settings/account')),
    feed: {
      create: lazyWithRetry(() => import('pages/settings/feed/create')),
      details: lazyWithRetry(() => import('pages/settings/feed/details')),
      update: lazyWithRetry(() => import('pages/settings/feed/update'))
    },
    integration: {
      dashboard: lazyWithRetry(() => import('pages/settings/integration/dashboard')),
      dynamics: {
        details: lazyWithRetry(() => import('pages/settings/integration/dynamics/details')),
        gate: lazyWithRetry(() => import('pages/settings/integration/dynamics/gate'))
      },
      chatgpt: lazyWithRetry(() => import('pages/settings/integration/chatgpt')),
      claude: lazyWithRetry(() => import('pages/settings/integration/claude')),
      email: lazyWithRetry(() => import('pages/settings/integration/email')),
      excel: lazyWithRetry(() => import('pages/settings/integration/excel')),
      happyCabbage: lazyWithRetry(() => import('pages/settings/integration/happy-cabbage')),
      powerBI: lazyWithRetry(() => import('pages/settings/integration/power-bi')),
      powerQuery: lazyWithRetry(() => import('pages/settings/integration/power-query')),
      quickBooksOnline: {
        details: lazyWithRetry(() => import('pages/settings/integration/quickbooks-online/details')),
        gate: lazyWithRetry(() => import('pages/settings/integration/quickbooks-online/gate'))
      },
      quantumLeaf: {
        details: lazyWithRetry(() => import('pages/settings/integration/quantum-leaf/details')),
        gate: lazyWithRetry(() => import('pages/settings/integration/quantum-leaf/gate'))
      }
    }
  },
  supplyChain: {
    product: {
      create: lazyWithRetry(() => import('pages/supply-chain/product/create')),
      dashboard: lazyWithRetry(() => import('pages/supply-chain/product/dashboard')),
      details: lazyWithRetry(() => import('pages/supply-chain/product/details')),
      update: lazyWithRetry(() => import('pages/supply-chain/product/update'))
    },
    supplier: {
      create: lazyWithRetry(() => import('pages/supply-chain/supplier/create')),
      dashboard: {
        route: '/suppliers/dashboard',
        page: lazyWithRetry(() => import('pages/supply-chain/supplier/dashboard'))
      },
      details: lazyWithRetry(() => import('pages/supply-chain/supplier/details')),
      update: lazyWithRetry(() => import('pages/supply-chain/supplier/update'))
    }
  },

  dashboard: {
    approval: lazyWithRetry(() => import('pages/dashboard/approval')),
    budgets: lazyWithRetry(() => import('pages/dashboard/budgets')),
    finance: lazyWithRetry(() => import('pages/dashboard/finance')),
    marketplace: lazyWithRetry(() => import('pages/dashboard/marketplace')),
    purchasing: lazyWithRetry(() => import('pages/dashboard/purchasing'))
  },

  report: {
    accruals: lazyWithRetry(() => import('pages/report/accruals')),
    orderHistory: lazyWithRetry(() => import('pages/report/order-history')),
    lineItems: lazyWithRetry(() => import('pages/report/line-items')),
    unfulfilledRequisitions: lazyWithRetry(() => import('pages/report/unfulfilled-requisitions')),
    punchOuts: lazyWithRetry(() => import('pages/report/punch-outs'))
  },

  search: lazyWithRetry(() => import('pages/search')),
  shop: lazyWithRetry(() => import('pages/shop'))
}

export const Routes = () => (
  <Suspense fallback={<Loader />}>
    <Switch>
      {/* Default - Route */}
      <Route exact path='/'>
        <Redirect to='/dashboard/marketplace' />
      </Route>
      {/* Account */}
      <Route exact path='/account'>
        <lazyLoadedPages.settings.account />
      </Route>
      <Route exact path='/account/profile'>
        <lazyLoadedPages.account.profile />
      </Route>
      <Route exact path='/account/profile/update'>
        <lazyLoadedPages.account.profileUpdate />
      </Route>
      {/* Accounting */}
      <Route exact path='/accounting/pay/invoice'>
        <lazyLoadedPages.accounting.pay.invoice />
      </Route>
      {/* Amazon */}
      <Route exact path='/amazon/product/:asin'>
        <lazyLoadedPages.amazon.product.details />
      </Route>
      <Route exact path='/amazon/shipment/tracking/:id'>
        <lazyLoadedPages.amazon.shipment.tracking />
      </Route>
      {/* Catalogs */}
      <Route exact path='/catalogs/create'>
        <lazyLoadedPages.organization.catalog.create />
      </Route>
      <Route exact path='/catalogs/dashboard'>
        <lazyLoadedPages.organization.catalog.dashboard />
      </Route>
      <Route exact path='/catalogs/details/:id'>
        <lazyLoadedPages.organization.catalog.details />
      </Route>
      <Route exact path='/catalogs/browser/:id?'>
        <lazyLoadedPages.shop />
      </Route>
      <Route exact path='/catalogs/update/:id'>
        <lazyLoadedPages.organization.catalog.update />
      </Route>
      {/* Dashboards */}
      <Route exact path='/dashboard/approval'>
        <lazyLoadedPages.dashboard.approval />
      </Route>
      <Route exact path='/dashboard/budgets'>
        <lazyLoadedPages.dashboard.budgets />
      </Route>
      <Route exact path='/dashboard/finance'>
        <lazyLoadedPages.dashboard.finance />
      </Route>
      <Route exact path='/dashboard/marketplace'>
        <lazyLoadedPages.dashboard.marketplace />
      </Route>
      <Route exact path='/dashboard/purchasing'>
        <lazyLoadedPages.dashboard.purchasing />
      </Route>
      {/* Departments */}
      <Route exact path='/departments/create/:locationId?'>
        <lazyLoadedPages.organization.department.create />
      </Route>
      <Route exact path='/departments/dashboard'>
        <lazyLoadedPages.organization.department.dashboard />
      </Route>
      <Route exact path='/departments/details/:id'>
        <lazyLoadedPages.organization.department.details />
      </Route>
      <Route exact path='/departments/update/:id'>
        <lazyLoadedPages.organization.department.update />
      </Route>
      {/* Feed */}
      <Route exact path='/feed/create'>
        <lazyLoadedPages.settings.feed.create />
      </Route>
      <Route exact path='/feed/details/:id'>
        <lazyLoadedPages.settings.feed.details />
      </Route>
      <Route exact path='/feed/update/:id'>
        <lazyLoadedPages.settings.feed.update />
      </Route>
      {/* Integrations */}
      <Route exact path='/integrations/dashboard'>
        <lazyLoadedPages.settings.integration.dashboard />
      </Route>
      {/* Integrations - Dynamics */}
      <Route exact path='/integrations/dynamics/gate'>
        <lazyLoadedPages.settings.integration.dynamics.gate />
      </Route>
      <Route exact path='/integrations/dynamics/details/:id'>
        <lazyLoadedPages.settings.integration.dynamics.details />
      </Route>
      {/* Integrations - ChatGPT */}
      <Route exact path='/integrations/chatgpt'>
        <lazyLoadedPages.settings.integration.chatgpt />
      </Route>
      {/* Integrations - Claude */}
      <Route exact path='/integrations/claude'>
        <lazyLoadedPages.settings.integration.claude />
      </Route>
      {/* Integrations - Email */}
      <Route exact path='/integrations/email'>
        <lazyLoadedPages.settings.integration.email />
      </Route>
      {/* Integrations - Excel */}
      <Route exact path='/integrations/excel'>
        <lazyLoadedPages.settings.integration.excel />
      </Route>
      {/* Integrations - Happy Cabbage */}
      <Route exact path='/integrations/happy-cabbage'>
        <lazyLoadedPages.settings.integration.happyCabbage />
      </Route>
      {/* Integrations - PowerBI */}
      <Route exact path='/integrations/power-bi'>
        <lazyLoadedPages.settings.integration.powerBI />
      </Route>
      {/* Integrations - Power Query */}
      <Route exact path='/integrations/power-query'>
        <lazyLoadedPages.settings.integration.powerQuery />
      </Route>
      {/* Integrations - QuantumLeaf */}
      <Route exact path='/integrations/quantum-leaf/details/:id'>
        <lazyLoadedPages.settings.integration.quantumLeaf.details />
      </Route>
      <Route exact path='/integrations/quantum-leaf/gate'>
        <lazyLoadedPages.settings.integration.quantumLeaf.gate />
      </Route>
      {/* Integrations - QuickBooks - Online */}
      <Route exact path='/integrations/quickbooks-online/gate'>
        <lazyLoadedPages.settings.integration.quickBooksOnline.gate />
      </Route>
      <Route exact path='/integrations/quickbooks-online/details/:id'>
        <lazyLoadedPages.settings.integration.quickBooksOnline.details />
      </Route>
      {/* Invoices */}
      <Route exact path='/invoices/dashboard'>
        <lazyLoadedPages.purchasing.invoices.dashboard />
      </Route>
      {/* Invoice Inquiry */}
      <Route exact path='/invoice-inquiry/details/:id'>
        <lazyLoadedPages.purchasing.invoices.inquiry />
      </Route>
      {/* Location */}
      <Route exact path='/locations/create'>
        <lazyLoadedPages.organization.location.create />
      </Route>
      <Route exact path='/locations/dashboard/:action?'>
        <lazyLoadedPages.organization.location.dashboard />
      </Route>
      <Route exact path='/locations/details/:id'>
        <lazyLoadedPages.organization.location.details />
      </Route>
      <Route exact path='/locations/update/:id'>
        <lazyLoadedPages.organization.location.update />
      </Route>
      {/* Requisition - Bulk Ordering */}
      <Route exact path='/requisitions/bulk-ordering/dashboard'>
        <lazyLoadedPages.purchasing.requisitions.bulkOrdering.dashboard />
      </Route>
      <Route exact path='/requisitions/bulk-ordering/details/:id'>
        <lazyLoadedPages.purchasing.requisitions.bulkOrdering.details />
      </Route>
      <Route exact path='/requisitions/bulk-ordering/edit/:id'>
        <lazyLoadedPages.purchasing.requisitions.bulkOrdering.edit />
      </Route>
      {/* Requisition */}
      <Route exact path='/requisitions/dashboard'>
        <lazyLoadedPages.purchasing.requisitions.dashboard />
      </Route>
      <Route path={'/requisitions/details/:uuid/:paymentStatus'}>
        <lazyLoadedPages.purchasing.requisitions.details />
      </Route>
      <Route path={'/requisitions/details/:uuid'}>
        <lazyLoadedPages.purchasing.requisitions.details />
      </Route>
      {/* DEPRECATE : Order Details Route */}
      <Route path={'/orders/details/:uuid'}>
        <lazyLoadedPages.purchasing.requisitions.details />
      </Route>
      <Route exact path='/requisitions/quick-order'>
        <lazyLoadedPages.purchasing.requisitions.quickorder />
      </Route>
      <Route exact path='/requisitions/submit/:uuid'>
        <lazyLoadedPages.purchasing.requisitions.submit />
      </Route>
      <Route exact path='/requisitions/update/:uuid'>
        <lazyLoadedPages.purchasing.requisitions.update />
      </Route>
      {/* Permission Group */}
      <Route exact path='/permission-group/dashboard'>
        <lazyLoadedPages.organization.permissionGroup.dashboard />
      </Route>
      <Route exact path='/permission-group/details/:id'>
        <lazyLoadedPages.organization.permissionGroup.details />
      </Route>
      <Route exact path='/permission-group/update/:id'>
        <lazyLoadedPages.organization.permissionGroup.update />
      </Route>
      {/* Product */}
      <Route exact path='/products/create/:supplierId?'>
        <lazyLoadedPages.supplyChain.product.create />
      </Route>
      <Route exact path='/products/dashboard'>
        <lazyLoadedPages.supplyChain.product.dashboard />
      </Route>
      <Route exact path='/products/details/:id'>
        <lazyLoadedPages.supplyChain.product.details />
      </Route>
      <Route exact path='/products/update/:id'>
        <lazyLoadedPages.supplyChain.product.update />
      </Route>
      {/* Punch Out */}
      <Route path={'/punch-outs/details/:id'}>
        <lazyLoadedPages.purchasing.punchOut.details />
      </Route>
      <Route path={'/punch-outs/details/:id/:action'}>
        <lazyLoadedPages.purchasing.punchOut.details />
      </Route>
      <Route exact path='/punch-outs/purchase'>
        <lazyLoadedPages.purchasing.punchOut.purchase />
      </Route>
      <Route exact path='/punch-outs/settings'>
        <lazyLoadedPages.purchasing.punchOut.settings />
      </Route>
      {/* Purchase Orders */}
      <Route exact path='/purchase-orders/dashboard'>
        <lazyLoadedPages.purchasing.purchaseOrders.dashboard />
      </Route>
      {/* Receiving */}
      <Route exact path='/receiving/dashboard'>
        <lazyLoadedPages.logistics.receiving.dashboard />
      </Route>
      <Route exact path='/receiving/details/:id?'>
        <lazyLoadedPages.logistics.receiving.details />
      </Route>
      {/* Report */}
      <Route exact path='/reports/accruals'>
        <lazyLoadedPages.report.accruals />
      </Route>
      <Route exact path='/reports/historical-orders'>
        <lazyLoadedPages.report.orderHistory />
      </Route>
      <Route exact path='/reports/order-history'>
        <lazyLoadedPages.report.orderHistory />
      </Route>
      <Route exact path='/reports/line-items'>
        <lazyLoadedPages.report.lineItems />
      </Route>
      <Route exact path='/reports/unfulfilled-requisitions'>
        <lazyLoadedPages.report.unfulfilledRequisitions />
      </Route>
      <Route exact path='/reports/open-orders'>
        <lazyLoadedPages.report.unfulfilledRequisitions />
      </Route>
      <Route exact path='/reports/punch-outs'>
        <lazyLoadedPages.report.punchOuts />
      </Route>
      {/* RFQ */}
      <Route exact path='/rfq/approve/:id'>
        <lazyLoadedPages.purchasing.rfq.approve />
      </Route>
      <Route exact path='/rfq/dashboard'>
        <lazyLoadedPages.purchasing.rfq.dashboard />
      </Route>
      <Route exact path='/rfq/details/:id'>
        <lazyLoadedPages.purchasing.rfq.details />
      </Route>
      <Route exact path='/rfq/update/:id'>
        <lazyLoadedPages.purchasing.rfq.update />
      </Route>
      {/* Search */}
      <Route exact path='/search/:query?'>
        <lazyLoadedPages.search />
      </Route>
      {/* Shipment */}
      <Route exact path='/shipments/dashboard'>
        <lazyLoadedPages.logistics.shipment.dashboard />
      </Route>
      <Route exact path='/shipments/details/:id'>
        <lazyLoadedPages.logistics.shipment.details />
      </Route>
      {/* Shop */}
      <Route exact path='/shop'>
        <lazyLoadedPages.shop />
      </Route>
      {/* Supplier */}
      <Route exact path='/suppliers/create'>
        <lazyLoadedPages.supplyChain.supplier.create />
      </Route>
      <Route exact path={lazyLoadedPages.supplyChain.supplier.dashboard.route}>
        <lazyLoadedPages.supplyChain.supplier.dashboard.page />
      </Route>
      <Route exact path='/suppliers/details/:id'>
        <lazyLoadedPages.supplyChain.supplier.details />
      </Route>
      <Route exact path='/suppliers/update/:id'>
        <lazyLoadedPages.supplyChain.supplier.update />
      </Route>
      {/* User */}
      <Route exact path='/users/create'>
        <lazyLoadedPages.organization.user.create />
      </Route>
      <Route exact path='/users/dashboard'>
        <lazyLoadedPages.organization.user.dashboard />
      </Route>
      <Route exact path='/users/details/:uuid'>
        <lazyLoadedPages.organization.user.details />
      </Route>
      <Route exact path='/users/update/:uuid'>
        <lazyLoadedPages.organization.user.update />
      </Route>
      <Route exact path='/blog/april-2026-rfq'>
        <PageApril2026RFQ />
      </Route>
      {/* 404 / Route Not Found */}
      <Route>
        <PageErr error={new Error('Page not found')} resetErrorBoundary={() => {}} />
      </Route>
    </Switch>
  </Suspense>
)
